import React, { useState, useEffect, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { Search, ArrowRight, Info, Building, Database, RefreshCw, Users } from 'lucide-react';
import { Header } from '../components/Header';
import { PWAInstallPrompt } from '../components/PWAInstallPrompt';
import { RotatingTagline } from '../components/RotatingTagline';
import { HomeSections } from '../components/HomeSections';
import { track, getHeroVariant } from '../lib/analytics';
import { markFlow } from '../lib/performance';
import { LoadingSpinner } from '../components/Loading';
import axios from 'axios';
import { track as trackKpi, trackOncePerSession } from '../lib/track';
import { isNative } from '../lib/capacitor-bridge';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const HomePage = () => {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const { setSearchQuery, setSearchResults, setIsLoading, setError, error } = useApp();
  const { isAuthenticated } = useAuth();
  const [query, setQuery] = useState('');
  const [publicChecks, setPublicChecks] = useState(0);
  const heroVariant = getHeroVariant();

  useEffect(() => {
    track('headline_variant_seen', { variant: heroVariant });
    track('homepage_view', { variant: heroVariant });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    axios.get(`${API_URL}/stats/checks-month`)
      .then((res) => { if (res.data.enabled) setPublicChecks(res.data.count); })
      .catch(() => {});
  }, []);
  const [searchLoading, setSearchLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    if (showSuggestions) window.dispatchEvent(new Event('cc:hide-pwa-prompt'));
    if (showSuggestions) trackOncePerSession('search_started');
  }, [showSuggestions]);

  useEffect(() => {
    trackOncePerSession('homepage_view');
  }, []);
  const [showTip, setShowTip] = useState(false);
  const inputRef = useRef(null);
  const busyRef = useRef(false);
  const debounceRef = useRef(null);
  const suggestionsRef = useRef(null);

  useEffect(() => { setIsLoading(false); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Auto-complete: fetch suggestions as user types (stale responses NEVER overwrite newer ones)
  const suggestReqId = useRef(0);
  const lastSuggestFail = useRef(0);
  const fetchSuggestions = useCallback(async (q) => {
    const reqId = ++suggestReqId.current;
    if (q.length < 2) { setSuggestions([]); return; }
    try {
      const res = await axios.get(`${API_URL}/search`, { params: { q }, timeout: 8000 });
      if (reqId !== suggestReqId.current) return; // a newer request was issued — discard
      setSuggestions((res.data || []).slice(0, 6));
      setShowSuggestions(true);
    } catch (err) {
      if (reqId === suggestReqId.current) setSuggestions([]);
      // Observabilité (max 1/30s) : diagnostiquer les échecs silencieux d'autocomplete par plateforme
      const now = Date.now();
      if (now - lastSuggestFail.current > 30000) {
        lastSuggestFail.current = now;
        track('suggest_failed', {
          status: String(err.response?.status || 0),
          code: String(err.code || 'unknown'),
          platform: window.Capacitor?.getPlatform?.() || 'web',
        });
      }
    }
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val.trim()), 350);
  };

  // Mobile web : au focus, remonte la zone de recherche au-dessus du clavier.
  // NATIF (iOS/Android) : on ne scrolle PAS ici — WKWebView exécute son propre
  // auto-scroll "révéler l'input" APRÈS le nôtre et l'écrase (monte puis redescend).
  // Le repositionnement natif se fait via keyboardDidShow (voir useEffect ci-dessous).
  const scrollHeroForKeyboard = () => {
    if (isNative()) return;
    if (window.innerWidth >= 768) return; // desktop intact
    const el = suggestionsRef.current;
    if (!el) return;
    const doScroll = () => el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const vv = window.visualViewport;
    if (vv) {
      let done = false;
      const handler = () => {
        if (done) return;
        done = true;
        vv.removeEventListener('resize', handler);
        doScroll();
      };
      vv.addEventListener('resize', handler); // déclenché à l'ouverture réelle du clavier
      setTimeout(handler, 350); // fallback unique (clavier déjà ouvert / pas de resize)
    } else {
      setTimeout(doScroll, 300);
    }
  };

  // Mode recherche compact — iOS natif uniquement : au focus, le contenu marketing
  // au-dessus du champ est replié pour que champ + CTA vivent en haut de page.
  // AUCUN scroll correctif : WebKit n'a plus rien à combattre (builds 52/53 = FAIL).
  const [compactSearch, setCompactSearch] = useState(false);
  const isIosNative = () =>
    (isNative() && window.Capacitor?.getPlatform?.() === 'ios') ||
    new URLSearchParams(window.location.search).get('compactSearchQA') === '1'; // hook QA navigateur

  // Primitive commune d'activation de la recherche — UNIQUE point d'entrée autorisé.
  // iOS natif, état initial : commit compact SYNCHRONE (flushSync) dans le geste
  // utilisateur → l'input réel est monté directement à sa géométrie finale → focus
  // immédiat. Input déjà monté : focus natif direct. AUCUN timer, AUCUN rAF.
  const activateSearch = () => {
    if (isIosNative() && !compactSearch) {
      flushSync(() => setCompactSearch(true));
    }
    inputRef.current?.focus({ preventScroll: true });
  };

  const handleSearchFocus = () => {
    if (suggestions.length > 0) setShowSuggestions(true);
    if (isIosNative()) {
      // Transition ATOMIQUE : le layout compact est commité de façon synchrone DANS
      // l'événement focus, AVANT l'auto-reveal WebKit et le resize clavier. La toute
      // première frame après focus est déjà la géométrie finale → zéro état intermédiaire.
      flushSync(() => setCompactSearch(true));
      window.scrollTo(0, 0); // le bloc recherche devient le haut de page
    } else {
      scrollHeroForKeyboard();
    }
  };

  // NATIF : ajustement Android via keyboardDidShow (comportement actuel conservé) ;
  // restauration du layout iOS à la fermeture réelle du clavier (keyboardDidHide).
  useEffect(() => {
    if (!isNative()) return;
    const platform = window.Capacitor?.getPlatform?.();
    let subShow, subHide;
    (async () => {
      try {
        const { Keyboard } = await import('@capacitor/keyboard');
        if (platform === 'android') {
          subShow = await Keyboard.addListener('keyboardDidShow', () => {
            if (document.activeElement !== inputRef.current) return;
            const btn = document.querySelector('[data-testid="search-button"]');
            if (!btn) return;
            const available = window.visualViewport?.height ?? window.innerHeight;
            const delta = btn.getBoundingClientRect().bottom - available + 12;
            if (delta > 0) window.scrollBy({ top: delta, behavior: 'smooth' });
          });
        }
        if (platform === 'ios') {
          subHide = await Keyboard.addListener('keyboardDidHide', () => {
            setCompactSearch(false);
          });
        }
      } catch { /* plugin indisponible : comportement par défaut */ }
    })();
    return () => { subShow?.remove?.(); subHide?.remove?.(); };
  }, []);

  const handleSearch = async (overrideQuery) => {
    const q = overrideQuery || (inputRef.current ? inputRef.current.value.trim() : query.trim());
    if (!q || busyRef.current) return;
    try {
      const shareSlug = sessionStorage.getItem('cc_share_slug');
      if (shareSlug && !sessionStorage.getItem('cc_share_search_sent')) {
        sessionStorage.setItem('cc_share_search_sent', '1');
        axios.post(`${API_URL}/share/track`, { slug: shareSlug, channel: 'web', event: 'share_first_search' });
      }
    } catch {}
    markFlow('search');
    track('hero_search_started', { variant: heroVariant });
    track('headline_variant_search', { variant: heroVariant });
    track('company_search');
    busyRef.current = true;
    setSearchLoading(true);
    setError(null);
    setSearchQuery(q);
    setShowSuggestions(false);

    const doSearch = async (attempt = 1) => {
      try {
        return await axios.get(`${API_URL}/search`, { params: { q }, timeout: 15000 });
      } catch (err) {
        if (attempt === 1 && (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED' || !err.response)) {
          await new Promise(r => setTimeout(r, 1500));
          return doSearch(2);
        }
        throw err;
      }
    };

    try {
      const response = await doSearch();
      setSearchResults(response.data);
      track('hero_search_completed', { variant: heroVariant });
      navigate('/results');
    } catch (err) {
      track('search_failed', {
        status: String(err.response?.status || 0),
        code: String(err.code || 'unknown'),
        msg: String(err.message || '').slice(0, 120),
        platform: window.Capacitor?.getPlatform?.() || 'web',
      });
      if (err.response?.status === 503) {
        setError(lang === 'fr'
          ? 'Le registre belge des entreprises (KBO) est temporairement indisponible. Réessayez dans quelques minutes.'
          : 'Het Belgisch ondernemingsregister (KBO) is tijdelijk onbeschikbaar. Probeer het over enkele minuten opnieuw.');
      } else if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED' || !err.response) {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          setError(lang === 'fr'
            ? 'Vous êtes hors ligne. Vérifiez votre connexion internet puis réessayez.'
            : 'U bent offline. Controleer uw internetverbinding en probeer opnieuw.');
        } else {
          setError(lang === 'fr'
            ? 'Impossible de joindre CheckCheck depuis ce réseau. Réessayez dans quelques secondes. Si le problème ne survient que sur ce réseau, celui-ci bloque peut-être l\'accès — réessayez depuis un autre réseau (Wi-Fi/4G) le temps que nous corrigions cela.'
            : 'CheckCheck is niet bereikbaar via dit netwerk. Probeer het over enkele seconden opnieuw. Als het probleem zich alleen op dit netwerk voordoet, blokkeert het mogelijk de toegang — probeer tijdelijk een ander netwerk (wifi/4G).');
        }
      } else {
        setError(lang === 'fr'
          ? 'La recherche a rencontré un problème temporaire. Veuillez réessayer.'
          : 'De zoekopdracht ondervond een tijdelijk probleem. Probeer opnieuw.');
      }
    } finally {
      busyRef.current = false;
      setSearchLoading(false);
    }
  };

  const selectSuggestion = (company) => {
    trackKpi('company_selected', { enterprise_number: company.enterprise_number });
    setQuery(company.name);
    setShowSuggestions(false);
    // Navigate directly to results with this company selected
    setSearchQuery(company.name);
    setSearchResults([company]);
    navigate('/results');
  };

  const c = {
    fr: {
      h1Line1: 'En apparence et sur les réseaux sociaux, tout va bien.',
      h1Line2: 'Et dans les comptes\u00A0?',
      h1Line3: 'CheckCheck regarde les chiffres.',
      tagline: 'La transparence financière n\'est plus réservée aux comptables.',
      subtitle: 'CheckCheck transforme les données financières des entreprises belges en informations simples et compréhensibles par tous.',
      freeLine: 'Analysez gratuitement la santé financière d\'une entreprise belge.',
      searchPlaceholder: 'Nom de l\'entreprise ou numéro de TVA',
      cta: 'Rechercher une entreprise',
      freeBadge: '100 % gratuit actuellement — sans abonnement ni frais cachés.',
      freeBadgeSub: 'Pas de carte bancaire • Pas d\'abonnement',
      trustLine: 'Plus de 1,9 million d\'entreprises belges analysées.',
      trustBadges: [
        { icon: Database, text: 'Données officielles' },
        { icon: RefreshCw, text: 'Mise à jour continue' },
        { icon: Users, text: 'Indépendants, PME & professionnels' },
      ],
      tipToggle: 'Vous ne trouvez pas l\'entreprise ?',
      tip: 'Le nom commercial d\'une entreprise ne correspond pas toujours à sa dénomination officielle. Par contre, le n\u00B0 de TVA est unique. Il figure sur tout devis, facture ou contrat. Si vous ne trouvez pas l\'entreprise par son nom, utilisez son n\u00B0 de TVA.',
    },
    nl: {
      h1Line1: 'Op het eerste gezicht en op sociale media lijkt alles perfect.',
      h1Line2: 'Maar wat zeggen de cijfers?',
      h1Line3: 'CheckCheck kijkt naar de cijfers.',
      tagline: 'Financiële transparantie is niet langer voorbehouden aan boekhouders.',
      subtitle: 'CheckCheck vertaalt de financiële gegevens van Belgische bedrijven naar eenvoudige, begrijpelijke informatie voor iedereen.',
      freeLine: 'Analyseer gratis de financiële gezondheid van een Belgisch bedrijf.',
      searchPlaceholder: 'Naam van het bedrijf of btw-nummer',
      cta: 'Zoek een bedrijf',
      freeBadge: '100% gratis op dit moment — zonder abonnement of verborgen kosten.',
      freeBadgeSub: 'Geen bankkaart • Geen abonnement',
      trustLine: 'Meer dan 1,9 miljoen Belgische bedrijven geanalyseerd.',
      trustBadges: [
        { icon: Database, text: 'Officiële gegevens' },
        { icon: RefreshCw, text: 'Continu bijgewerkt' },
        { icon: Users, text: 'Zelfstandigen, KMO\'s & professionals' },
      ],
      tipToggle: 'Bedrijf niet gevonden?',
      tip: 'De handelsnaam van een bedrijf komt niet altijd overeen met de officiële naam. Het BTW-nummer is echter uniek. Het staat op elke offerte, factuur of contract. Als u het bedrijf niet vindt op naam, gebruik dan het BTW-nummer.',
    },
  }[lang];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 flex flex-col items-center px-4 sm:px-6 pt-28 sm:pt-32 pb-10 max-w-lg mx-auto w-full">

        {/* Mode recherche compact (iOS natif, clavier ouvert) : replie le contenu marketing */}
        <div className={compactSearch ? 'hidden' : 'contents'} data-testid="hero-marketing-block">
        {/* H1 — brand statement (Tâche 1: hiérarchie APPARENCE → DOUTE → RÉPONSE) */}
        <h1
          className="text-center font-['Outfit'] animate-fade-in-up mb-4"
          data-testid="hero-title"
        >
          <span className="block text-lg sm:text-xl font-semibold text-[#102A43] leading-snug" data-testid="hero-line1">
            {c.h1Line1}
          </span>
          <span className="block text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#102A43] leading-tight mt-2" data-testid="hero-line2">
            {c.h1Line2}
          </span>
          <span className="block text-lg sm:text-xl font-bold text-[#2563EB] leading-snug mt-3" data-testid="hero-line3">
            {c.h1Line3}
          </span>
        </h1>

        {/* Brand tagline */}
        <p className="text-base sm:text-lg font-semibold text-[#2563EB] text-center leading-snug mb-4 animate-fade-in-up" style={{ animationDelay: '40ms' }} data-testid="hero-tagline">
          {c.tagline}
        </p>

        {/* Short explanation */}
        <p className="text-sm sm:text-base text-slate-500 text-center leading-relaxed mb-3 animate-fade-in-up" style={{ animationDelay: '80ms' }} data-testid="hero-subtitle">
          {c.subtitle}
        </p>

        {/* Free for everyone */}
        <p className="text-sm sm:text-base font-bold text-[#16A34A] text-center mb-7 animate-fade-in-up" style={{ animationDelay: '120ms' }} data-testid="hero-free-line">
          {c.freeLine}
        </p>
        </div>

        {/* SEARCH BAR — main element, visually dominant */}
        <div className="w-full animate-fade-in-up relative z-40 scroll-mt-20" style={{ animationDelay: '200ms', marginTop: compactSearch ? 'clamp(16px, 6vh, 48px)' : undefined }} ref={suggestionsRef} data-testid="search-form">
          <div className="relative">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            {isIosNative() && !compactSearch ? (
              <button
                type="button"
                onClick={activateSearch}
                aria-label={c.searchPlaceholder}
                className="w-full h-16 pl-14 pr-5 bg-white border-2 border-slate-200 rounded-2xl text-base text-left transition-all duration-300"
                data-testid="search-activator"
              >
                <span className={`block truncate ${query ? 'text-[#172033]' : 'text-slate-400'}`}>{query || c.searchPlaceholder}</span>
              </button>
            ) : (
            <input
              ref={inputRef}
              type="text"
              enterKeyHint="search"
              autoCorrect="off"
              autoCapitalize="none"
              value={query}
              onChange={handleInputChange}
              onFocus={handleSearchFocus}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
              placeholder={c.searchPlaceholder}
              className="w-full h-16 pl-14 pr-5 bg-white border-2 border-slate-200 rounded-2xl text-[#172033] text-base placeholder:text-slate-400 focus:border-[#2563EB]/60 focus:bg-[#FFFFFF] focus:shadow-[0_0_30px_rgba(37,99,235,0.08)] transition-all duration-300"
              data-testid="search-input"
            />
            )}
          </div>

          {/* Auto-complete suggestions */}
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl overflow-hidden shadow-2xl max-h-80 overflow-y-auto" style={{ backgroundColor: '#FFFFFF' }} data-testid="suggestions-dropdown">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => selectSuggestion(s)}
                  className="w-full px-4 py-3 text-left hover:bg-slate-100 transition-colors flex items-center gap-3 border-b border-slate-100 last:border-0"
                  data-testid={`suggestion-${i}`}
                >
                  <Building className="w-4 h-4 text-slate-500 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-[#172033] font-medium truncate">{s.name}</p>
                    <p className="text-xs text-slate-400 truncate">
                      {s.enterprise_number}{s.address ? ` — ${s.address.split(',')[0]}` : ''}
                    </p>
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}

          {/* CTA button */}
          <button
            type="button"
            onMouseDown={() => setShowSuggestions(false)}
            onClick={() => handleSearch()}
            className={`w-full h-14 mt-3 bg-[#2563EB] text-white font-bold text-base rounded-full flex items-center justify-center gap-2 hover:bg-[#1D4ED8] active:scale-[0.98] transition-all shadow-[0_4px_20px_rgba(37,99,235,0.2)] ${(!query.trim() && !searchLoading) ? 'opacity-50' : ''}`}
            data-testid="search-button"
          >
            {searchLoading
              ? <LoadingSpinner size="sm" />
              : <><Search className="w-5 h-5" />{c.cta}</>
            }
          </button>

          {/* Free badge */}
          <p className="text-sm text-[#2563EB] font-bold text-center mt-2.5" data-testid="home-free-badge">
            {c.freeBadge}
          </p>
          <p className="text-xs text-slate-500 text-center mt-0.5" data-testid="home-free-badge-sub">
            {c.freeBadgeSub}
          </p>

          {publicChecks > 0 && (
            <p className="text-xs text-slate-400 text-center mt-1.5" data-testid="home-public-counter">
              {lang === 'fr'
                ? `${publicChecks.toLocaleString('fr-BE')} entreprises vérifiées ce mois-ci.`
                : `${publicChecks.toLocaleString('nl-BE')} bedrijven gecontroleerd deze maand.`}
            </p>
          )}

          {/* Error */}
          {error && (
            <div className="mt-3 p-3 rounded-xl bg-red-500/10 border border-red-500/20" data-testid="search-error">
              <p className="text-xs text-red-600 text-center">{typeof error === 'string' ? error : (error?.message || '')}</p>
            </div>
          )}
        </div>

        {/* Tip toggle */}
        <div className="w-full mt-3 mb-10 animate-fade-in-up" style={{ animationDelay: '250ms' }}>
          <button
            onClick={() => setShowTip(!showTip)}
            className="flex items-center gap-1.5 mx-auto text-[11px] text-slate-400 hover:text-slate-500 transition-colors"
            data-testid="vat-tip-toggle"
          >
            <Info className="w-3.5 h-3.5" />
            <span>{c.tipToggle}</span>
          </button>
          {showTip && (
            <div className="mt-3 p-3.5 rounded-xl bg-[#2563EB]/5 border border-[#2563EB]/10 text-xs text-slate-500 leading-relaxed text-center animate-fade-in-up" data-testid="vat-tip-content">
              {c.tip}
            </div>
          )}
        </div>

        {/* Rotating tagline */}
        <div className="w-full mb-8 animate-fade-in-up" style={{ animationDelay: '300ms' }}>
          <RotatingTagline lang={lang} />
        </div>

        {/* Product sections */}
        <HomeSections
          lang={lang}
          onSearchFocus={() => {
            window.dispatchEvent(new Event('cc:hide-pwa-prompt'));
            if (!isIosNative()) window.scrollTo({ top: 0, behavior: 'smooth' });
            activateSearch();
          }}
        />

        {/* ───── TRUST SECTION ───── */}
        <div className="w-full text-center animate-fade-in-up" style={{ animationDelay: '350ms' }} data-testid="trust-section">
          {/* Big trust number */}
          <p className="text-sm font-semibold text-slate-600 mb-5" data-testid="trust-line">
            {c.trustLine}
          </p>

          {/* Trust badges */}
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-5 mb-8">
            {c.trustBadges.map((badge, i) => (
              <div key={i} className="flex items-center justify-center gap-2 text-slate-400">
                <badge.icon className="w-3.5 h-3.5" />
                <span className="text-xs">{badge.text}</span>
              </div>
            ))}
          </div>

          {/* Source */}
          <p className="text-[10px] text-slate-500 mb-4">{t('dataSource')}</p>
        </div>

        {/* FAQ gratuité — indexable */}
        <div className="max-w-md mx-auto mt-8 mb-4 text-center" data-testid="home-faq-free">
          <h2 className="text-sm font-semibold text-slate-600">
            {lang === 'fr' ? 'CheckCheck est-il gratuit ?' : 'Is CheckCheck gratis?'}
          </h2>
          <p className="text-xs text-slate-400 mt-1">
            {lang === 'fr'
              ? 'Oui. CheckCheck est entièrement gratuit, sans carte bancaire, sans abonnement et sans limite de consultation.'
              : 'Ja. CheckCheck is volledig gratis, zonder bankkaart, abonnement of gebruikslimiet.'}
          </p>
        </div>

        {/* Footer links */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/pour-qui')}
            className="text-xs text-slate-400 hover:text-[#2563EB] transition-colors"
            data-testid="homepage-pourqui-link"
          >
            {lang === 'fr' ? 'Pour qui ?' : 'Voor wie?'}
          </button>
          <span className="text-slate-500">|</span>
          <button
            onClick={() => navigate('/contact')}
            className="text-xs text-slate-400 hover:text-[#2563EB] transition-colors"
            data-testid="homepage-contact-link"
          >
            {lang === 'fr' ? 'Nous contacter' : 'Contacteer ons'}
          </button>
          <span className="text-slate-500">|</span>
          <button
            onClick={() => navigate('/legal')}
            className="text-xs text-slate-400 hover:text-[#2563EB] transition-colors"
            data-testid="homepage-legal-link"
          >
            {lang === 'fr' ? 'Mentions légales' : 'Wettelijke vermeldingen'}
          </button>
          <span className="text-slate-500">|</span>
          <button
            onClick={() => navigate('/confidentialite')}
            className="text-xs text-slate-400 hover:text-[#2563EB] transition-colors"
            data-testid="homepage-privacy-link"
          >
            {lang === 'fr' ? 'Confidentialité' : 'Privacy'}
          </button>
        </div>
      </main>

      <PWAInstallPrompt />
    </div>
  );
};
